import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import Input from '../../../components/Input';
import TextArea from '../../../components/TextArea';

function AddVehicleTypeModal({ open, onClose, onConfirm, vehicleTypeData, editMode = false }) {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
  });

  const [errors, setErrors] = useState({});

  // Initialize form data when dialog opens or when vehicleTypeData changes
  useEffect(() => {
    if (open) {
      if (editMode && vehicleTypeData) {
        setFormData({
          id: vehicleTypeData.id,
          code: vehicleTypeData.code || '',
          name: vehicleTypeData.name || '',
          description: vehicleTypeData.description || '',
        });
      } else {
        setFormData({
          code: '',
          name: '',
          description: '',
        });
      }
      setErrors({});
    }
  }, [open, vehicleTypeData, editMode]);

  // Handle input change
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Validate field in real-time
    const newErrors = { ...errors };

    if (field === 'code') {
      if (!value.trim()) {
        newErrors.code = t('vehicleTypes.addDialog.codeRequired');
      } else if (value.length > 50) {
        newErrors.code = t('vehicleTypes.addDialog.codeMaxLength');
      } else {
        delete newErrors.code;
      }
    } else if (field === 'name') {
      if (!value.trim()) {
        newErrors.name = t('vehicleTypes.addDialog.nameRequired');
      } else if (value.length > 100) {
        newErrors.name = t('vehicleTypes.addDialog.nameMaxLength');
      } else {
        delete newErrors.name;
      }
    } else if (field === 'description') {
      if (value && value.length > 255) {
        newErrors.description = t('vehicleTypes.addDialog.descriptionMaxLength');
      } else {
        delete newErrors.description;
      }
    }

    setErrors(newErrors);
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    return formData.code.trim() !== '' && formData.name.trim() !== '';
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = t('vehicleTypes.addDialog.codeRequired');
    } else if (formData.code.length > 50) {
      newErrors.code = t('vehicleTypes.addDialog.codeMaxLength');
    }

    if (!formData.name.trim()) {
      newErrors.name = t('vehicleTypes.addDialog.nameRequired');
    } else if (formData.name.length > 100) {
      newErrors.name = t('vehicleTypes.addDialog.nameMaxLength');
    }

    if (formData.description && formData.description.length > 255) {
      newErrors.description = t('vehicleTypes.addDialog.descriptionMaxLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle confirm
  const handleConfirm = () => {
    if (validateForm()) {
      onConfirm(formData);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: editMode ? 'primary.main' : 'success.main',
        }}
      >
        {editMode ? <EditIcon /> : <AddIcon />}
        {editMode ? t('vehicleTypes.editDialog.title') : t('vehicleTypes.addDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          {/* Code */}
          <Input
            label={t('vehicleTypes.addDialog.code')}
            value={formData.code}
            onChange={(e) => handleChange('code', e.target.value)}
            required
            error={!!errors.code}
            helperText={errors.code}
            placeholder={t('vehicleTypes.addDialog.code')}
            id="code"
            name="code"
            data-testid="vehicleType-modal-input-code"
          />

          {/* Name */}
          <Input
            label={t('vehicleTypes.addDialog.name')}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            error={!!errors.name}
            helperText={errors.name}
            placeholder={t('vehicleTypes.addDialog.name')}
            id="name"
            name="name"
            data-testid="vehicleType-modal-input-name"
          />

          {/* Description */}
          <TextArea
            label={t('vehicleTypes.addDialog.description')}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={3}
            placeholder={t('vehicleTypes.addDialog.description')}
            error={!!errors.description}
            helperText={errors.description}
            id="description"
            name="description"
            data-testid="vehicleType-modal-input-description"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          id="cancel"
          name="cancel"
          data-testid="vehicleType-modal-button-cancel"
        >
          {t('vehicleTypes.addDialog.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!isFormValid() || Object.values(errors).some(error => error !== '')}
          startIcon={editMode ? <EditIcon /> : <AddIcon />}
          id="confirm"
          name="confirm"
          data-testid="vehicleType-modal-button-confirm"
        >
          {editMode ? t('vehicleTypes.editDialog.confirm') : t('vehicleTypes.addDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddVehicleTypeModal;
